import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
    Skeleton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';

import { fetcher, securityEventApi } from '../../lib/api';
import styles from './SecurityEvents.module.scss';

const PAGE_SIZE = 30;

const TYPE_STYLES = {
    brute_force: { background: '#ffebee', color: '#c62828' },
    super_admin_granted: { background: '#fce4ec', color: '#ad1457' },
    company_from_1819: { background: '#e8f5e9', color: '#2e7d32' },
    company_created_manual: { background: '#e3f2fd', color: '#1565c0' },
    auth_job_failures: { background: '#fff3e0', color: '#e65100' },
    multi_ip: { background: '#f3e5f5', color: '#6a1b9a' },
    config_drift: { background: '#eceff1', color: '#455a64' },
    mass_companies_deleted: { background: '#ffebee', color: '#b71c1c' },
    mass_companies_disabled: { background: '#fff3e0', color: '#ef6c00' },
    mass_offers_deleted: { background: '#fce4ec', color: '#c2185b' },
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

const SecurityEvents = () => {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [type, setType] = useState('all');
    const [rows, setRows] = useState([]);
    const [expandedId, setExpandedId] = useState(null);

    const listUrl = securityEventApi.list({
        page,
        limit: PAGE_SIZE,
        ...(type !== 'all' ? { type } : {}),
    });

    const { data, error, isLoading } = useSWR(listUrl, fetcher, {
        refreshInterval: 15000,
    });

    useEffect(() => {
        if (!data?.results) return;
        setRows((prev) => {
            if (page === 1) return data.results;
            const byId = new Map(prev.map((r) => [r.id, r]));
            data.results.forEach((r) => byId.set(r.id, r));
            return Array.from(byId.values());
        });
    }, [data, page]);

    const filters = useMemo(
        () => [
            { id: 'all', label: t('security_events.filter_all') },
            { id: 'brute_force', label: t('security_events.type_brute_force') },
            { id: 'super_admin_granted', label: t('security_events.type_super_admin') },
            { id: 'company_from_1819', label: t('security_events.type_company_1819') },
            { id: 'auth_job_failures', label: t('security_events.type_auth_job') },
            { id: 'multi_ip', label: t('security_events.type_multi_ip') },
            { id: 'config_drift', label: t('security_events.type_config') },
        ],
        [t]
    );

    const totalCount = data?.count ?? rows.length;
    const hasMore = rows.length > 0 && rows.length < totalCount;

    const typeLabel = (typeId) => {
        const map = {
            brute_force: t('security_events.type_brute_force'),
            super_admin_granted: t('security_events.type_super_admin'),
            company_from_1819: t('security_events.type_company_1819'),
            company_created_manual: t('security_events.type_company_manual'),
            auth_job_failures: t('security_events.type_auth_job'),
            multi_ip: t('security_events.type_multi_ip'),
            config_drift: t('security_events.type_config'),
            mass_companies_deleted: t('security_events.type_mass_companies_deleted'),
            mass_companies_disabled: t('security_events.type_mass_companies_disabled'),
            mass_offers_deleted: t('security_events.type_mass_offers_deleted'),
        };
        return map[typeId] || typeId;
    };

    return (
        <GridContainer>
            <GridItem xs={12}>
                <SparaTitle title={t('menu.security_events')} />
            </GridItem>
            <GridItem xs={12}>
                <Card>
                    <CardBody>
                        <div className={styles.listHeader}>
                            <div className={styles.listHeaderCount}>
                                {totalCount} {t('menu.security_events').toLowerCase()}
                            </div>
                            <div className={styles.filters}>
                                {filters.map((f) => (
                                    <button
                                        key={f.id}
                                        type="button"
                                        className={`${styles.filterChip} ${
                                            type === f.id ? styles.filterChipActive : ''
                                        }`}
                                        onClick={() => {
                                            setType(f.id);
                                            setPage(1);
                                            setRows([]);
                                        }}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div className={styles.empty}>
                                {error?.response?.data?.message || t('common.error')}
                            </div>
                        )}

                        <div className={styles.tableWrap}>
                            <Table>
                                <TableHead className={styles.tableHead}>
                                    <TableRow>
                                        <TableCell>{t('security_events.col_type')}</TableCell>
                                        <TableCell>{t('security_events.col_headline')}</TableCell>
                                        <TableCell>{t('security_events.col_email')}</TableCell>
                                        <TableCell>{t('security_events.col_created')}</TableCell>
                                        <TableCell>{t('actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody className={styles.tableBody}>
                                    {isLoading && page === 1 && rows.length === 0 &&
                                        Array.from({ length: 6 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {Array.from({ length: 5 }).map((__, j) => (
                                                    <TableCell key={j}>
                                                        <Skeleton variant="text" width={80} />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    {!isLoading && rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <div className={styles.empty}>
                                                    {t('security_events.empty')}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {rows.map((row) => (
                                        <React.Fragment key={row.id}>
                                            <TableRow>
                                                <TableCell>
                                                    <span
                                                        className={styles.badge}
                                                        style={
                                                            TYPE_STYLES[row.type] ||
                                                            TYPE_STYLES.config_drift
                                                        }
                                                    >
                                                        {typeLabel(row.type)}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div>{row.headline}</div>
                                                    <div className={styles.progressLabel}>
                                                        {row.summary}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {row.email_sent
                                                        ? t('security_events.email_yes')
                                                        : t('security_events.email_no')}
                                                </TableCell>
                                                <TableCell>{formatDate(row.created_at)}</TableCell>
                                                <TableCell>
                                                    <button
                                                        type="button"
                                                        className={styles.actionBtnPrimary}
                                                        onClick={() =>
                                                            setExpandedId(
                                                                expandedId === row.id
                                                                    ? null
                                                                    : row.id
                                                            )
                                                        }
                                                    >
                                                        {expandedId === row.id
                                                            ? t('security_events.hide_details')
                                                            : t('security_events.show_details')}
                                                    </button>
                                                </TableCell>
                                            </TableRow>
                                            {expandedId === row.id && (
                                                <TableRow>
                                                    <TableCell colSpan={5}>
                                                        <div className={styles.exceptionBox}>
                                                            {JSON.stringify(row.details || {}, null, 2)}
                                                        </div>
                                                        {row.action_url && (
                                                            <div className={styles.progressLabel}>
                                                                {row.action_url}
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>

                        {hasMore && (
                            <div className={styles.loadMore}>
                                <SparaButton color="primary" onClick={() => setPage((p) => p + 1)}>
                                    {t('security_events.load_more')}
                                </SparaButton>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default SecurityEvents;
